import React, { useState, useRef, useEffect } from "react";
import styles from "./ChatTestPage.module.scss";
import axiosInstance from "../../api/axios";
import { useTranslation } from "react-i18next";
import { movieTitleHandler } from "../../utils/helpers";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "../../constants/routes";
import { genresData } from "../../constants/structure";

interface Movie {
  id: number;
  title: string;
  title_ru: string;
  title_ua: string;
  title_ar: string;
  poster_path: string;
  backdrop_path: string;
  release_date: string;
  vote_average: number;
  imdb_rating?: number | null;
  rotten_tomatoes_rating?: number | null;
  genre_ids: number[];
  overview: string;
  overview_ru: string;
  overview_ua: string;
}

interface ChatMessage {
  id: number;
  role: "user" | "assistant";
  content: string;
  movie_ids: number[];
  movies: Movie[];
  created_at: string;
}

const IMG_BASE = process.env.REACT_APP_IMG_BASE_URL;

const ChatTestPage: React.FC = () => {
  const { i18n, t } = useTranslation();
  const navigate = useNavigate();

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [conversationId, setConversationId] = useState<number | null>(null);
  const [sessionKey] = useState(
    () => localStorage.getItem("chatSessionKey") || crypto.randomUUID()
  );
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    localStorage.setItem("chatSessionKey", sessionKey);
  }, [sessionKey]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const langMap: Record<string, string> = {
    en: "en-US",
    ru: "ru-RU",
    uk: "uk-UA",
    ar: "ar-SA",
  };

  const sendMessage = async () => {
    if (!input.trim() || loading) return;

    const userText = input.trim();
    setInput("");

    const userMsg: ChatMessage = {
      id: Date.now(),
      role: "user",
      content: userText,
      movie_ids: [],
      movies: [],
      created_at: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, userMsg]);
    setLoading(true);

    try {
      const payload: any = {
        message: userText,
        language: langMap[i18n.language] || "en-US",
      };
      if (conversationId) {
        payload.conversation_id = conversationId;
      }

      const { data } = await axiosInstance.post("chat/", payload, {
        headers: { "X-Chat-Session": sessionKey },
      });

      setConversationId(data.conversation_id);

      const assistantMsg: ChatMessage = {
        ...data.message,
        movies: data.movies || [],
      };
      setMessages((prev) => [...prev, assistantMsg]);
    } catch (err: any) {
      const errorMsg: ChatMessage = {
        id: Date.now() + 1,
        role: "assistant",
        content: err.response?.data?.detail || "Something went wrong. Please try again.",
        movie_ids: [],
        movies: [],
        created_at: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, errorMsg]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const newChat = () => {
    setMessages([]);
    setConversationId(null);
  };

  const formatRating = (r?: number | null) =>
    r != null ? r.toFixed(1) : "—";

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <h1 className={styles.logo}>HIVIEWER</h1>
        <div className={styles.headerActions}>
          <button className={styles.newChatBtn} onClick={newChat}>
            New chat
          </button>
        </div>
      </div>

      <div className={styles.chatArea}>
        {messages.length === 0 && (
          <div className={styles.welcome}>
            <h2>Hi there!</h2>
            <p>I'm your personal movie guide. Describe what you want to watch.</p>
          </div>
        )}

        {messages.map((msg) => (
          <div
            key={msg.id}
            className={`${styles.message} ${
              msg.role === "user" ? styles.userMessage : styles.assistantMessage
            }`}
          >
            <div className={styles.messageContent}>
              <p className={styles.messageText}>{msg.content}</p>
            </div>

            {msg.movies && msg.movies.length > 0 && (
              <div className={styles.moviesGrid}>
                {msg.movies.map((movie) => {
                  const title = movieTitleHandler(i18n.language, movie) || movie.title;
                  const year = movie.release_date?.substring(0, 4);
                  const genres = movie.genre_ids
                    ?.slice(0, 2)
                    .map((gid) => genresData.find((g) => g.id === gid)?.name)
                    .filter(Boolean)
                    .map((name) => t(name!))
                    .join(", ");

                  return (
                    <div
                      key={movie.id}
                      className={styles.movieCard}
                      onClick={() => navigate(`${ROUTES.DETAILS}/${movie.id}`)}
                    >
                      {movie.poster_path ? (
                        <img
                          className={styles.poster}
                          src={`${IMG_BASE}${movie.poster_path}`}
                          alt={title}
                          loading="lazy"
                        />
                      ) : (
                        <div className={styles.posterPlaceholder} />
                      )}
                      <div className={styles.cardInfo}>
                        <h4 className={styles.movieTitle}>{title}</h4>
                        <div className={styles.meta}>
                          {year && <span className={styles.year}>{year}</span>}
                          {genres && (
                            <span className={styles.genres}>{genres}</span>
                          )}
                        </div>
                        <div className={styles.ratings}>
                          {movie.imdb_rating != null && (
                            <span className={styles.ratingBadge}>
                              IMDB {formatRating(movie.imdb_rating)}
                            </span>
                          )}
                          {movie.rotten_tomatoes_rating != null && (
                            <span className={styles.ratingBadge}>
                              RT {Math.round(movie.rotten_tomatoes_rating)}%
                            </span>
                          )}
                          {!movie.imdb_rating && !movie.rotten_tomatoes_rating && (
                            <span className={styles.ratingBadge}>
                              TMDB {formatRating(movie.vote_average)}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ))}

        {loading && (
          <div className={`${styles.message} ${styles.assistantMessage}`}>
            <div className={styles.messageContent}>
              <div className={styles.typing}>
                <span /><span /><span />
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      <div className={styles.inputArea}>
        <div className={styles.inputWrapper}>
          <input
            className={styles.input}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Describe what you want to watch..."
            disabled={loading}
            maxLength={2000}
          />
          <button
            className={styles.sendBtn}
            onClick={sendMessage}
            disabled={!input.trim() || loading}
          >
            &#8593;
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChatTestPage;
